import React from 'react';

import { injectIntl, type WithIntlProps, type WrappedComponentProps } from 'react-intl-next';

import { type DispatchAnalyticsEvent } from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { ToolbarDropdownWrapper } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { token } from '@atlaskit/tokens';
import { ColorPicker, createBuiltInColorMap } from '@atlassian/color-picker';

import type { CompanyHubTextColorPlugin } from '../../../../companyHubTextColorPluginType';
import { changeColor as changeColorWithAnalytics } from '../../../../editor-commands/change-color';
import type { CompanyHubTextColorPluginState } from '../../../../pm-plugins';

import { messages } from './messages';

export enum ToolbarType {
	PRIMARY = 'primary',
	FLOATING = 'floating',
}

export interface State {
	isOpen: boolean;
	isOpenedByKeyboard: boolean;
}

export interface Props {
	disabled?: boolean;
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent;
	editorView: EditorView;
	isReducedSpacing?: boolean;
	pluginInjectionApi: ExtractInjectionAPI<CompanyHubTextColorPlugin> | undefined;
	pluginState: CompanyHubTextColorPluginState;
	popupsBoundariesElement?: HTMLElement;
	popupsMountPoint?: HTMLElement;
	popupsScrollableElement?: HTMLElement;
	toolbarType: ToolbarType;
}

export const ToolbarCompanyHubTextColor: React.FC<WithIntlProps<Props & WrappedComponentProps>> & {
    WrappedComponent: React.ComponentType<Props & WrappedComponentProps>;
} = injectIntl(
	({ pluginState, editorView, pluginInjectionApi, disabled }: Props & WrappedComponentProps) => {
		const blackWhiteColorMap = createBuiltInColorMap({
			knownColors: [token('color.text.inverse'), token('color.text')],
		});
		const changeTextColor = React.useCallback(
			(color: string) => {
				if (color && !disabled) {
					changeColorWithAnalytics(color, pluginInjectionApi?.analytics?.actions)(
						editorView.state,
						editorView.dispatch,
					);
				}
				editorView?.focus();
				return false;
			},
			[disabled, editorView, pluginInjectionApi?.analytics?.actions],
		);

		return (
			<ToolbarDropdownWrapper data-testid="company-hub-text-color-picker">
				<ColorPicker
					analyticsSource="companyHubToolbarTextColor"
					builtInColorMap={blackWhiteColorMap}
					onChange={changeTextColor}
					value={pluginState.color || pluginState.defaultColor}
					isDisabled={disabled || pluginState.disabled}
					pickerTitle={messages.colorPickerTitle}
					showCustomPalette
					showTextIcon
					enableBorderLessStyle
				/>
			</ToolbarDropdownWrapper>
		);
	},
);
